// src/components/AIRichText.ts
import "./AIRichText.css";

export type AIRichTextVariant = "overview" | "chat";

export interface AIRichTextOptions {
  variant?: AIRichTextVariant;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderInline(value: string): string {
  let html = escapeHtml(value);

  // Safe because escaping happens first. Only formatting markers introduced by
  // the model are converted; arbitrary HTML remains escaped.
  html = html.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  html = html.replace(/`([^`\n]+)`/g, "<code>$1</code>");

  return html;
}

function isUnordered(line: string): boolean {
  return /^\s*[-*•]\s+/.test(line);
}

function isOrdered(line: string): boolean {
  return /^\s*\d{1,3}[.)]\s+/.test(line);
}

function stripUnordered(line: string): string {
  return line.replace(/^\s*[-*•]\s+/, "").trim();
}

function stripOrdered(line: string): string {
  return line.replace(/^\s*\d{1,3}[.)]\s+/, "").trim();
}

function headingLevel(line: string): 0 | 3 | 4 {
  if (/^\s*#{2,3}\s+/.test(line)) return 3;
  if (/^\s*#{4,6}\s+/.test(line)) return 4;
  return 0;
}

function stripHeading(line: string): string {
  return line.replace(/^\s*#{2,6}\s+/, "").trim();
}

export function renderAIRichText(
  value: string,
  options: AIRichTextOptions = {},
): string {
  const variant = options.variant || "chat";
  const normalized = String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!normalized) return "";

  const lines = normalized.split("\n");
  const blocks: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;

  const flushParagraph = () => {
    const text = paragraph.join(" ").replace(/\s+/g, " ").trim();
    paragraph = [];
    if (text) {
      blocks.push(`<p>${renderInline(text)}</p>`);
    }
  };

  const flushList = () => {
    if (!listType || !listItems.length) {
      listType = null;
      listItems = [];
      return;
    }

    const tag = listType;
    blocks.push(
      `<${tag}>${listItems
        .map((item) => `<li>${renderInline(item)}</li>`)
        .join("")}</${tag}>`,
    );
    listType = null;
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    const level = headingLevel(line);
    if (level) {
      flushParagraph();
      flushList();
      const tag = level === 3 ? "h3" : "h4";
      blocks.push(`<${tag}>${renderInline(stripHeading(line))}</${tag}>`);
      continue;
    }

    if (isUnordered(line)) {
      flushParagraph();
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listItems.push(stripUnordered(line));
      continue;
    }

    if (isOrdered(line)) {
      flushParagraph();
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listItems.push(stripOrdered(line));
      continue;
    }

    if (listType) flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();

  return `<div class="aye-ai-rich-text aye-ai-rich-text-${variant}">${blocks.join("")}</div>`;
}
